/**
 * Menu grant helpers for Admin → Permissions → Roles.
 * CommonJS so both the React app and root Jest can load this file.
 *
 * A stored parent id covers every descendant. A child id can be stored on
 * its own. A child cannot be excluded while an ancestor is stored.
 */

function asMenuArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }
  return [];
}

function indexMenu(items, parentId, path, acc) {
  const index = acc || {
    byId: new Map(),
    parentOf: new Map(),
    childrenOf: new Map(),
    allIds: new Set(),
  };
  const trail = path || [];
  const list = items || [];
  for (const item of list) {
    if (!item || !item.id) continue;
    const nextPath = trail.concat(item.label || item.id);
    index.byId.set(item.id, {
      id: item.id,
      label: item.label || item.id,
      pathLabel: nextPath.join(' → '),
      hideInProject: !!item.hideInProject,
      requiresProject: !!item.requiresProject,
      requiresEstimate: !!item.requiresEstimate,
    });
    index.allIds.add(item.id);
    if (parentId) index.parentOf.set(item.id, parentId);
    const childIds = (item.subItems || []).map((sub) => sub && sub.id).filter(Boolean);
    index.childrenOf.set(item.id, childIds);
    if (item.subItems && item.subItems.length) {
      indexMenu(item.subItems, item.id, nextPath, index);
    }
  }
  return index;
}

function ancestorIds(id, parentOf) {
  const out = [];
  let cur = parentOf.get(id);
  while (cur) {
    out.push(cur);
    cur = parentOf.get(cur);
  }
  return out;
}

function descendantIds(id, childrenOf) {
  const out = [];
  const stack = [...(childrenOf.get(id) || [])];
  while (stack.length) {
    const child = stack.pop();
    out.push(child);
    stack.push(...(childrenOf.get(child) || []));
  }
  return out;
}

function grantSet(menuItems) {
  return new Set(asMenuArray(menuItems).filter((id) => id && id !== '*'));
}

function coveredByAncestor(id, grants, parentOf) {
  const set = grants instanceof Set ? grants : grantSet(grants);
  return ancestorIds(id, parentOf).some((ancestorId) => set.has(ancestorId));
}

/** A stored id covers itself and any menu id that starts with "<id>-". */
function grantCoversMenu(grant, menuId) {
  return menuId === grant || String(menuId).startsWith(`${grant}-`);
}

function isRecognizedGrant(id, index) {
  if (!id || id === '*') return true;
  if (index.allIds.has(id)) return true;
  for (const known of index.allIds) {
    if (grantCoversMenu(id, known)) return true;
  }
  return false;
}

/**
 * True when some other stored grant already includes this menu
 * (a parent in the tree, or a prefix such as "estimation").
 */
function coveredByStoredGrant(id, grants, index) {
  const set = grants instanceof Set ? grants : grantSet(grants);
  if (coveredByAncestor(id, set, index.parentOf)) return true;
  for (const grant of set) {
    if (grant !== id && grantCoversMenu(grant, id)) return true;
  }
  return false;
}

function directlyGranted(id, grants) {
  const set = grants instanceof Set ? grants : grantSet(grants);
  return set.has(id);
}

/**
 * Check a node to store it and drop now-redundant descendants.
 * Uncheck a node to remove only that id.
 * No change when an ancestor already covers the node.
 */
function toggleMenuGrant(menuItems, itemId, index) {
  const set = grantSet(menuItems);
  if (coveredByStoredGrant(itemId, set, index)) return [...set];
  if (set.has(itemId)) {
    set.delete(itemId);
  } else {
    set.add(itemId);
    descendantIds(itemId, index.childrenOf).forEach((desc) => set.delete(desc));
  }
  return [...set];
}

function nodeMatchesQuery(node, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const hay = `${node.label} ${node.id} ${node.pathLabel}`.toLowerCase();
  return q.split(/\s+/).every((token) => hay.includes(token));
}

function matchingMenuIds(query, index) {
  const q = String(query || '').trim();
  if (!q) return null;
  const hits = [];
  for (const node of index.byId.values()) {
    if (nodeMatchesQuery(node, q)) hits.push(node.id);
  }
  return hits;
}

/**
 * True when this role's stored grants cover a menu the query names,
 * including via a parent grant, a child grant, or "*".
 * Empty query matches every role. A query that matches no menu matches none.
 */
function roleMatchesMenuQuery(menuItems, query, index) {
  const hits = matchingMenuIds(query, index);
  if (hits === null) return true;
  if (hits.length === 0) return false;
  const items = asMenuArray(menuItems);
  if (items.includes('*')) return true;
  const set = grantSet(items);
  return hits.some((id) => {
    if (set.has(id)) return true;
    if (ancestorIds(id, index.parentOf).some((ancestorId) => set.has(ancestorId))) return true;
    if (descendantIds(id, index.childrenOf).some((desc) => set.has(desc))) return true;
    for (const grant of set) {
      if (grantCoversMenu(grant, id)) return true;
    }
    return false;
  });
}

function unknownMenuIds(menuItems, index) {
  return asMenuArray(menuItems).filter((id) => !isRecognizedGrant(id, index));
}

/** Stored ids that are not themselves menus, but still cover menus by prefix. */
function prefixGrantIds(menuItems, index) {
  return asMenuArray(menuItems).filter((id) => (
    id !== '*' && !index.allIds.has(id) && isRecognizedGrant(id, index)
  ));
}

function catalogPayload(index) {
  const ids = [];
  const labels = [];
  for (const node of index.byId.values()) {
    ids.push(node.id);
    labels.push({ id: node.id, label: node.label, path: node.pathLabel });
  }
  return { ids, labels };
}

function branchMatches(item, query, index) {
  const q = String(query || '').trim();
  if (!q) return true;
  const node = index.byId.get(item.id);
  if (node && nodeMatchesQuery(node, q)) return true;
  return (item.subItems || []).some((sub) => branchMatches(sub, q, index));
}

module.exports = {
  asMenuArray,
  indexMenu,
  ancestorIds,
  descendantIds,
  coveredByAncestor,
  coveredByStoredGrant,
  isRecognizedGrant,
  directlyGranted,
  toggleMenuGrant,
  nodeMatchesQuery,
  matchingMenuIds,
  roleMatchesMenuQuery,
  unknownMenuIds,
  prefixGrantIds,
  catalogPayload,
  branchMatches,
};
